import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

const ConversationMessageThoughtSchema = z.object({
  text: z.string(),
  createdAt: z.coerce.date(),
});

const ConversationMessageAgentRunTaskSchema = z.object({
  taskType: z.literal('agent_run'),
  agentRunId: z.string(),
});

const ConversationMessageRequestedModelSchema = z.enum([
  'basic',
  'standard',
  'thorough',
]);

const BaseConversationMessageSchema = z.object({
  conversationMessageId: z.string(),
  conversationId: z.string(),
  role: z.enum(['user', 'assistant']),
  thoughts: ConversationMessageThoughtSchema.array(),
  tasks: ConversationMessageAgentRunTaskSchema.array(),
  requestedModel: ConversationMessageRequestedModelSchema.nullable()
    .default(null)
    .optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ConversationMessageSchema = BaseConversationMessageSchema.extend({
  content: z.object({
    text: z.string(),
  }),
  contentType: z.literal('plaintext'),
});

export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const ConversationMessagesSchema = z.array(ConversationMessageSchema);

export type ConversationMessages = z.infer<typeof ConversationMessagesSchema>;

export const ConversationMessagesQuerySchema =
  createOffsetPaginationRequestQuerySchema(
    z.enum(['createdAt', 'updatedAt']),
  ).extend({
    conversationIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    roles: commaDelimitedList(z.string().array()).optional(),
  });

export type ConversationMessagesQuery = z.infer<
  typeof ConversationMessagesQuerySchema
>;

export const CreateConversationMessageSchema = ConversationMessageSchema.pick({
  content: true,
  contentType: true,
  conversationId: true,
  requestedModel: true,
}).extend({
  role: z.enum(['user']),
});

export type CreateConversationMessage = z.infer<
  typeof CreateConversationMessageSchema
>;

export const DeleteManyConversationMessagesQuerySchema = z.object({
  conversationIds: commaDelimitedList(PrimaryKeySchema.array().min(1)),
});

export type DeleteManyConversationMessagesQuery = z.infer<
  typeof DeleteManyConversationMessagesQuerySchema
>;
